import { useId } from 'react';

import { LIBRARY_STATES, type LibraryStateFilter } from '../searchParams';
import { narrowingLabel } from '../labels';

/**
 * How much of the catalog is on screen: everything, what this workspace holds,
 * or what has an update waiting (spec 105 FR-001).
 *
 * A radio group of visually hidden inputs behind label pills, the same
 * `.segmented` pattern the usage-limit period field uses. Native radios are
 * what give arrow-key movement, a single tab stop and a checked state that
 * assistive technology reads, none of it reimplemented.
 *
 * Deliberately not a second tab strip: the kinds above already own that role,
 * and two `tablist`s on one page would leave a keyboard reader guessing which
 * one the arrow keys are talking to (research R1).
 */
export function StateFilter({
  value,
  onChange,
}: {
  value: LibraryStateFilter;
  onChange: (next: LibraryStateFilter) => void;
}) {
  const name = useId();
  const legendId = `${name}-legend`;

  return (
    // The full width on a phone, its four options sharing it (spec 278).
    <div className="flex items-center gap-2 max-md:w-full">
      <span id={legendId} className="sr-only">
        Show
      </span>
      <div
        role="radiogroup"
        aria-labelledby={legendId}
        className="border-border bg-muted/40 flex overflow-hidden rounded-md border p-0.5 max-md:flex-1"
      >
        {LIBRARY_STATES.map((option) => {
          const optionId = `${name}-${option}`;
          return (
            <span key={option} className="relative max-md:flex-1">
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option}
                checked={value === option}
                onChange={() => {
                  onChange(option);
                }}
                className="peer absolute inset-0 opacity-0"
              />
              <label
                htmlFor={optionId}
                className="peer-checked:bg-card peer-checked:text-primary peer-focus-visible:ring-ring text-muted-foreground hover:text-foreground peer-checked:shadow-raised block cursor-pointer rounded-sm px-2.5 py-1 pointer-coarse:py-3 text-center text-xs font-medium whitespace-nowrap transition-colors peer-checked:font-semibold peer-focus-visible:ring-3 peer-focus-visible:outline-none"
              >
                {narrowingLabel(option)}
              </label>
            </span>
          );
        })}
      </div>
    </div>
  );
}
